import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  List, // Make sure List is imported
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  Box,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import Slide from "@mui/material/Slide";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MuiDropdownBottomSheet = ({
  label,
  options = [],
  onChange,
  value = null,
  error = false,
  helperText = "",
  required = false,
  bottomSheetHeight = "50vh",
  subtitle,
  fieldName,
}) => {
  const [open, setOpen] = useState(false);
  const [localSelection, setLocalSelection] = useState(value);
  const [hasOpened, setHasOpened] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dialogContentRef = useRef(null);

  useEffect(() => {
    setLocalSelection(value);
  }, [value]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (dialogContentRef.current) {
          dialogContentRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (dialogContentRef.current) {
      const firstItem = dialogContentRef.current.querySelector("li");
      if (firstItem) {
        if (searchTerm) {
          firstItem.scrollIntoView({ behavior: "smooth", block: "nearest" });
        } else if (localSelection) {
          const selectedItemElement = dialogContentRef.current.querySelector(`[data-value="${localSelection}"]`);
          if (selectedItemElement) {
            selectedItemElement.scrollOfView({ behavior: "smooth", block: "nearest" });
          }
        }
      }
    }
  }, [searchTerm, localSelection]);

  const handleOpen = () => {
    setOpen(true);
    setHasOpened(true);
    setSearchTerm("");
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm("");
  };

  const handleOk = () => {
    if (onChange) {
      onChange(fieldName, localSelection);
    }
    setOpen(false);
  };

  const handleSelect = (val) => {
    setLocalSelection(val);
    if (onChange) {
      onChange(fieldName, val);
    }
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (["ArrowUp", "ArrowDown", "Enter"].includes(event.key)) {
      event.preventDefault();
    }

    if (event.key === "Backspace") {
      setSearchTerm((prev) => prev.slice(0, -1));
    } else if (event.key === "Escape") {
      searchTerm ? setSearchTerm("") : handleClose();
    } else if (event.key.length === 1 && event.key.match(/[a-z0-9 ]/i)) {
      setSearchTerm((prev) => prev + event.key);
    }
  };

  const selectedOption = options.find(opt => opt.value === localSelection);
  const selectedLabel = selectedOption ? selectedOption.label : "";

  const displayErrorMessage =
    required && !open && hasOpened && !localSelection;

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <TextField
        label={label}
        value={selectedLabel}
        placeholder="Velg alternativ"
        onClick={handleOpen}
        fullWidth
        error={displayErrorMessage || error}
        helperText={displayErrorMessage ? helperText || "Dette feltet er påkrevd" : " "}
        InputLabelProps={{ shrink: !!selectedLabel }}
        InputProps={{
          readOnly: true,
          endAdornment: <ArrowDropDownIcon sx={{ marginRight: '12px' }} />,
          style: {
            cursor: "pointer",
            fontStyle: selectedLabel ? "normal" : "italic",
            color: selectedLabel ? "#000" : "#6F7979",
          },
        }}
        sx={{
          '& .MuiInputLabel-root': {
            color: '#3F4949',
            fontFamily: 'Inter',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#00696D',
            fontFamily: 'Inter',
          },
          '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: 'calc(100% - 50px)',
          },
          '& .MuiInputBase-root': {
            padding: '0px',
          },
          '& .MuiInput-input': {
            padding: '16.5px 14px',
          },
        }}
        FormHelperTextProps={{
          sx: { height: '24px', margin: '0px' }
        }}
      />

      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          style: {
            borderRadius: "16px 16px 0 0",
            width: "100%",
            maxWidth: "600px",
            position: "fixed",
            bottom: 0,
            margin: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0px -4px 10px rgba(0, 0, 0, 0.15)",
            backgroundColor: "#F2F4F4",
            height: bottomSheetHeight,
          },
        }}
        BackdropProps={{ onClick: handleClose }}
      >
        {/* Grab handle */}
        <Box sx={{ textAlign: "center", padding: "16px", cursor: "grab" }}>
          <Box sx={{
            width: 32,
            height: 4,
            opacity: 0.4,
            backgroundColor: "#6F7979",
            borderRadius: 2,
            margin: "0 auto",
          }} />
        </Box>

        {/* Close Icon and Subtitle (now main title) */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px',
        }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[700],
              marginRight: '0px',
            }}
          >
            <CloseIcon />
          </IconButton>
          {subtitle && (
            <Typography
              variant="h6"
              sx={{
                marginLeft: '4px',
                flexGrow: 1,
                textAlign: 'left',
                color: 'var(--M3-sys-light-on-surface, #191C1C)',
                fontFamily: 'Inter',
                fontSize: '22px',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: '28px',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        <DialogContent
          ref={dialogContentRef}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          sx={{
            overflowY: "auto",
            flexGrow: 1,
            padding: 0,
            "&:focus": {
              outline: "none",
            },
          }}
        >
          <List
            sx={{
              paddingTop: '0px', // Set padding-top to 0 to remove the extra space
              paddingBottom: '0px', // Also consider setting padding-bottom to 0 if not needed
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <ListItem
                  key={opt.value}
                  button
                  onClick={() => handleSelect(opt.value)}
                  sx={{
                    height: 50,
                    px: 2,
                    "&:hover": { backgroundColor: "#f5f5f5" },
                    backgroundColor: localSelection === opt.value ? '#E0E0E0' : 'transparent',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                  data-value={opt.value}
                >
                  <ListItemText
                    primary={opt.label}
                    sx={{
                      color: localSelection === opt.value ? '#00696D' : 'inherit',
                      flexGrow: 1,
                      color: 'var(--M3-sys-light-on-surface, #191C1C)',
                      fontFamily: 'Inter',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      lineHeight: '24px',
                      letterSpacing: '0.5px'
                    }}
                  />
                  <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
                    {localSelection === opt.value && (
                      <CheckIcon sx={{ color: '#00696D' }} />
                    )}
                  </ListItemIcon>
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary="No options available"
                  sx={{ textAlign: "center", fontStyle: "italic", color: "#6F7979" }}
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MuiDropdownBottomSheet;